import React, {
  useEffect,
  useContext,
  useRef,
  useState,
  Suspense,
} from "react";
import Header from "./Header";
import { CabinetContext } from "./CabinetContext";
import Graph from "./Graph/Graph";
import "./styles/Cabinet.css";
import { formatDate } from "../helpers/helpers";
import { Thought, Dictionary } from "../helpers/types";
import Output from "editorjs-react-renderer";
import axios from "axios";

const SidebarImage = React.lazy(() => import("./SidebarImage"));

function Cabinet() {
  const context = useContext(CabinetContext);
  const { thoughts, sundays, grabbedData, view } = context;
  const containerRef = useRef<HTMLInputElement>(null);
  const [selectedThought, setSelectedThought] = useState<Thought | null>(null);
  const [isMobile, setMobile] = useState<boolean>(false);
  const [sidebar, setSidebar] = useState<Thought | null>(null);
  const [labels, setLabels] = useState<Dictionary<string[]>>({});
  const [selection, setSelection] = useState<string>("all");
  const [thoughtsLoaded, setThoughtsLoaded] = useState<boolean>(false);
  const [imgRatio, setImgRatio] = useState<number>(1);
  const [sundaysWithThoughts, setSundaysWithThoughts] = useState<string[]>([]);
  const [sidebarWidth, setSidebarWidth] = useState(300);
  const isResizing = useRef(false);

  useEffect(() => {
    if (!thoughtsLoaded) {
      if (view !== "calendar") {
        setLabels({});
      }
    }

    if (
      (!thoughtsLoaded && view === "calendar" && thoughts && sundays) ||
      (view !== "calendar" && thoughts)
    ) {
      let dict: Dictionary<string[]> = {};
      for (let i = 0; i < thoughts.length; i++) {
        if (thoughts[i].label) {
          let all_labels = thoughts[i].label.split(", ");
          for (let j = 0; j < all_labels.length; j++) {
            if (!dict[all_labels[j]]) {
              dict[all_labels[j]] = [thoughts[i].id];
            }
          }
        }
      }
      setLabels(dict);

      if (dict[selection] === undefined) {
        setSelection("all");
      }
    }
  }, [thoughtsLoaded, thoughts, view, sundays]);

  // set mobile
  const handleResize = () => {
    if (containerRef.current) {
      if (containerRef.current.offsetWidth < 800) {
        setMobile(true);
      } else {
        setMobile(false);
      }
    }
  };

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    handleResize();
  }, [containerRef]);

  useEffect(() => {
    if (sidebar && thoughts) {
      for (let i = 0; i < thoughts!.length; i++) {
        if (thoughts![i].id === sidebar.id) {
          if (thoughts[i].id !== selectedThought?.id) {
            setSelectedThought(null);
            setSelectedThought(thoughts![i]);
            break;
          }
        }
      }
    } else {
      setSelectedThought(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebar, thoughts]);

  useEffect(() => {
    const fetchSundaysWithThoughts = async () => {
      try {
        const res = await axios.get(
          "https://us-central1-thought-cabinet.cloudfunctions.net/app/getSundaysWithThoughts"
        );
        setSundaysWithThoughts(res.data.sundays);

        // Preload images
        if (thoughts) {
          thoughts.forEach((thought) => {
            if (thought.type === "image") {
              const img = new Image();
              img.src = thought.url;
            }
          });
        }
      } catch (error) {
        console.error("Error fetching Sundays with thoughts:", error);
      }
    };

    fetchSundaysWithThoughts();
  }, []);

  const handleMouseDown = () => {
    isResizing.current = true;
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isResizing.current) {
      setSidebarWidth(e.clientX);
    }
  };

  const handleMouseUp = () => {
    isResizing.current = false;
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  return (
    <div className="thought-cabinet-container">
      <div
        className={
          "header-wrapper " + (view === "calendar" ? "calendar" : "topic")
        }
      >
        <Header />
        {!thoughtsLoaded && (
          <div className="loading">
            <div className="loading-text">
              <div className="animation">Connecting the dots</div>
            </div>
          </div>
        )}
        {true && (
          <div className="label-dropdown">
            <input
              type="radio"
              id="all"
              name="label"
              value="all"
              defaultChecked={selection === "all"}
              onChange={() => {
                setSelection("all");
              }}
            />
            <label
              htmlFor="all"
              style={{
                fontStyle: selection === "all" ? "normal" : "italic",
              }}
            >
              &nbsp;All&nbsp;
            </label>

            {Object.keys(labels).map((label, i) => (
              <div className="label-wrapper" key={label + i}>
                <input
                  type="radio"
                  id={label}
                  key={"radio_" + label}
                  name="label"
                  value={label}
                  defaultChecked={selection === label}
                  onChange={() => {
                    setSelection(label);
                  }}
                />
                <label
                  htmlFor={label}
                  style={{
                    fontStyle: selection === label ? "normal" : "italic",
                  }}
                >
                  &nbsp;{label}&nbsp;
                </label>
              </div>
            ))}
          </div>
        )}
      </div>

      {thoughts && sundays && (
        <div className="visual-container">
          {thoughtsLoaded && (
            <>
              <div
                className={"sidebar " + (sidebar ? "open" : "closed")}
                style={{ width: sidebar ? sidebarWidth : 0 }}
              >
                {selectedThought && (
                  <div className="sidebar-wrapper">
                    <div className="sidebar-header">
                      <div className="thought-date">
                        {formatDate(selectedThought.date)[0]}
                      </div>
                      <div className="close" onClick={() => setSidebar(null)} />
                    </div>
                    <div className="sidebar-content">
                      {selectedThought.type === "image" ? (
                        <>
                          <Suspense
                            key={selectedThought?.id}
                            fallback={<div>Loading...</div>}
                          >
                            <SidebarImage thought={selectedThought} />
                          </Suspense>
                        </>
                      ) : selectedThought.type === "rich_text" ||
                        selectedThought.type === "text" ? (
                        <Output data={selectedThought.content} />
                      ) : selectedThought.type === "link" ? (
                        <a href={selectedThought.url}>
                          <Suspense fallback={<div>Loading...</div>}>
                            <SidebarImage thought={selectedThought} />
                          </Suspense>
                          <div className="thought-link-text">
                            <div className="thought-link-title">
                              {selectedThought.title}
                            </div>
                            <p>{selectedThought.description}</p>
                          </div>
                        </a>
                      ) : null}
                    </div>
                    <div
                      className="resize-handle-container"
                      onMouseDown={handleMouseDown}
                    >
                      <div className="resize-handle" />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          <div
            ref={containerRef}
            className="svg-container"
            style={{
              width: sidebar ? `calc(100% - ${sidebarWidth}px)` : "100%",
            }}
          >
            {grabbedData && (
              <Graph
                isMobile={isMobile}
                thoughts={thoughts}
                setSidebar={setSidebar}
                containerRef={containerRef}
                selection={selection}
                setThoughtsLoaded={setThoughtsLoaded}
                setImgRatio={setImgRatio}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Cabinet;
